import * as React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ImagePlus, Loader2, X } from 'lucide-react';
import { woocommerceApi } from '@/lib/api/services/woocommerce';
import { toast } from '@/hooks/use-toast';
import { showApiError } from '@/lib/errors';

/**
 * The WooCommerce product form — state, submit and fields — extracted from
 * `WooCommerceProducts` so more than one host can render THE SAME form.
 *
 * Three hosts need it now:
 *
 *   1. `AddProductModal` on the legacy Commerce page's WooCommerce tab
 *      (`/home/commerce/products?tab=woocommerce`), which is where these fields
 *      come from and still the canonical presentation.
 *   2. `EditProductModal` beside it, which already shared the field set and
 *      keeps doing so — it seeds from an existing product and PUTs instead of
 *      POSTing, so it brings its own state and only borrows the fields.
 *   3. `commerce-v2/AddProductFlow` — the drawer whose "Publish to" picker now
 *      offers WooCommerce, and therefore has to show WooCommerce's own fields
 *      rather than the WhatsApp-catalog ones.
 *
 * Same split as `shopify/shopifyProductForm`: a hook plus a dumb fields
 * component, NOT a second dialog, because the hosts frame it differently — the
 * modal has a title bar and a two-button footer, the drawer has a "Publish to"
 * picker above it and a full-width footer.
 *
 * WHY A HOOK AND NOT PROP-DRILLED STATE: images upload to WordPress media
 * ASYNCHRONOUSLY while the form is still being filled, so "is this form
 * submittable" is not a property of the text fields alone — it also means "has
 * every upload settled". Handing a host raw setters and asking it to re-derive
 * that is how the two copies drift.
 */

export interface WooImageItem {
  url: string;
  uploading?: boolean;
  error?: string;
}

export interface WooProductFormValue {
  name: string;
  regular_price: string;
  sale_price: string;
  sku: string;
  status: string;
  stock_status: string;
  stock_quantity: string;
  short_description: string;
}

export const EMPTY_WOO_PRODUCT_FORM: WooProductFormValue = {
  name: '',
  regular_price: '',
  sale_price: '',
  sku: '',
  status: 'publish',
  stock_status: 'instock',
  stock_quantity: '',
  short_description: '',
};

export const WOO_STATUS_OPTIONS = [
  { value: 'publish', label: 'Published' },
  { value: 'draft', label: 'Draft' },
  { value: 'pending', label: 'Pending' },
  { value: 'private', label: 'Private' },
];

export const WOO_STOCK_STATUS_OPTIONS = [
  { value: 'instock', label: 'In Stock' },
  { value: 'outofstock', label: 'Out of Stock' },
  { value: 'onbackorder', label: 'On Backorder' },
];

/**
 * The body both the create and the update call send.
 *
 * `stock_quantity` is only included when the user typed one, and it drags
 * `manage_stock: true` along with it — WooCommerce ignores a quantity on a
 * product that is not stock-managed, so sending one without the flag silently
 * does nothing. An empty box therefore means "unmanaged", not "zero".
 */
export function buildWooProductPayload(
  form: WooProductFormValue,
  images: WooImageItem[],
): Record<string, unknown> {
  const data: Record<string, unknown> = {
    name: form.name.trim(),
    regular_price: form.regular_price,
    sale_price: form.sale_price || '',
    sku: form.sku,
    status: form.status,
    stock_status: form.stock_status,
    short_description: form.short_description,
    // A failed upload left its local object-URL in the list so the user can see
    // which one broke; sending that blob: URL would store a dead image.
    images: images.filter((img) => !img.error).map((img) => ({ src: img.url })),
  };
  if (form.stock_quantity !== '') {
    data.stock_quantity = parseInt(form.stock_quantity, 10);
    data.manage_stock = true;
  }
  return data;
}

/** Everything a host needs to render the fields and drive the create. */
export interface WooProductForm {
  value: WooProductFormValue;
  set: (key: keyof WooProductFormValue, val: string) => void;
  images: WooImageItem[];
  setImages: React.Dispatch<React.SetStateAction<WooImageItem[]>>;
  businessId: string;
  currency: string;
  loading: boolean;
  /** True while any image is still uploading — submit must wait for it. */
  uploading: boolean;
  /** Name is the one field WooCommerce will not create without. */
  canSubmit: boolean;
  reset: () => void;
  /**
   * Resolves `{ ok: true, productId }` on success — the caller may need to act
   * on the product it just created (publishing it to the Meta catalog targets
   * exactly that product rather than sweeping the store).
   */
  submit: () => Promise<{ ok: boolean; productId?: string }>;
}

export function useWooProductForm({
  open,
  businessId,
  currency = 'USD',
}: {
  open: boolean;
  businessId: string;
  currency?: string;
}): WooProductForm {
  const [value, setValue] = React.useState<WooProductFormValue>({
    ...EMPTY_WOO_PRODUCT_FORM,
  });
  const [images, setImages] = React.useState<WooImageItem[]>([]);
  const [loading, setLoading] = React.useState(false);

  const set = (key: keyof WooProductFormValue, val: string) =>
    setValue((prev) => ({ ...prev, [key]: val }));

  const reset = React.useCallback(() => {
    setValue({ ...EMPTY_WOO_PRODUCT_FORM });
    setImages([]);
  }, []);

  const uploading = images.some((img) => img.uploading);

  const submit = async (): Promise<{ ok: boolean; productId?: string }> => {
    if (!businessId) return { ok: false };
    if (!value.name.trim()) {
      toast({
        title: 'Name required',
        description: 'Please enter a product name.',
        variant: 'warning',
      });
      return { ok: false };
    }
    if (uploading) {
      toast({
        title: 'Please wait',
        description: 'Images are still uploading.',
        variant: 'warning',
      });
      return { ok: false };
    }

    setLoading(true);
    try {
      const created = await woocommerceApi.createProduct(businessId, {
        // WooCommerce's other product types (variable, grouped, external) each
        // need their own extra fields; this form collects a simple product.
        type: 'simple',
        ...buildWooProductPayload(value, images),
      });
      toast({
        variant: 'success',
        title: 'Product Created',
        description: `"${value.name}" was added to your store.`,
      });
      // The route replies `{ success, product }`; the id is the WooCommerce
      // product id the catalog publish keys on.
      const createdId =
        created?.product?.id != null ? String(created.product.id) : undefined;
      reset();
      return { ok: true, productId: createdId };
    } catch (e) {
      showApiError(e, { fallback: { title: 'Create Failed' } });
      return { ok: false };
    } finally {
      setLoading(false);
    }
  };

  // The drawer stays mounted between opens; without this a Cancel would leave
  // the previous attempt's values (and its uploaded images) waiting to reappear.
  React.useEffect(() => {
    if (!open) return;
    reset();
  }, [open, reset]);

  return {
    value,
    set,
    images,
    setImages,
    businessId,
    currency,
    loading,
    uploading,
    canSubmit: Boolean(value.name.trim()) && !uploading,
    reset,
    submit,
  };
}

/**
 * Images → Name → Prices → SKU/Stock → Status → Description. The whole body of
 * the WooCommerce product form, with no dialog or drawer chrome of its own.
 *
 * Takes the pieces rather than a `WooProductForm`, because `EditProductModal`
 * seeds from an existing product and owns its own state — it borrows the fields
 * without the create hook.
 */
export function WooProductFormFields({
  form,
  currency,
  onChange,
  images,
  onImagesChange,
  businessId,
}: {
  form: WooProductFormValue;
  currency: string;
  onChange: (key: keyof WooProductFormValue, val: string) => void;
  images: WooImageItem[];
  onImagesChange: React.Dispatch<React.SetStateAction<WooImageItem[]>>;
  businessId: string;
}) {
  /**
   * The file input is driven by a real <label htmlFor>, not a programmatic
   * click on a `display:none` input. That older shape depends on the browser
   * carrying user activation through a synthetic click on an element that is
   * not in the layout, which Safari/iOS and confined builds (snap/flatpak,
   * embedded webviews) can refuse outright — inside a focus-trapped dialog or
   * drawer most of all, which is what both hosts are.
   */
  const fileInputId = React.useId();

  const handleFiles = async (files: FileList) => {
    const newItems: WooImageItem[] = Array.from(files).map((f) => ({
      url: URL.createObjectURL(f),
      uploading: true,
    }));
    onImagesChange((prev) => [...prev, ...newItems]);

    const uploaded: WooImageItem[] = await Promise.all(
      Array.from(files).map(async (file, i) => {
        try {
          const url = await woocommerceApi.uploadImage(businessId, file);
          return { url, uploading: false };
        } catch (e) {
          return {
            url: newItems[i].url,
            uploading: false,
            error: e instanceof Error ? e.message : 'Upload failed',
          };
        }
      }),
    );

    onImagesChange((prev) => {
      // Replace the uploading placeholders with final results.
      const kept = prev.filter((img) => !img.uploading);
      return [...kept, ...uploaded];
    });
  };

  const removeImage = (idx: number) =>
    onImagesChange((prev) => prev.filter((_, i) => i !== idx));

  return (
    <div className="space-y-4">
      {/* Images */}
      <div className="space-y-2">
        <Label>Product Images</Label>
        <div className="flex flex-wrap gap-2">
          {images.map((img, i) => (
            <div
              key={i}
              className="relative w-20 h-20 rounded-md border overflow-hidden group"
            >
              <img src={img.url} alt="" className="w-full h-full object-cover" />
              {img.uploading && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <Loader2 className="h-4 w-4 text-white animate-spin" />
                </div>
              )}
              {img.error && (
                <div className="absolute inset-0 bg-destructive/70 flex items-center justify-center p-1">
                  <span className="text-[9px] text-white text-center leading-tight">
                    Upload failed
                  </span>
                </div>
              )}
              {!img.uploading && (
                <button
                  type="button"
                  onClick={() => removeImage(i)}
                  className="absolute top-0.5 right-0.5 bg-black/60 hover:bg-black/80 rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="h-3 w-3 text-white" />
                </button>
              )}
            </div>
          ))}

          <input
            id={fileInputId}
            type="file"
            accept="image/*"
            multiple
            className="peer sr-only"
            onChange={(e) => {
              if (e.target.files?.length) {
                handleFiles(e.target.files);
                e.target.value = '';
              }
            }}
          />
          <label
            htmlFor={fileInputId}
            className="w-20 h-20 rounded-md border-2 border-dashed border-muted-foreground/30 hover:border-muted-foreground/60 flex flex-col items-center justify-center gap-1 text-muted-foreground hover:text-foreground transition-colors cursor-pointer peer-focus-visible:ring-2 peer-focus-visible:ring-ring peer-focus-visible:ring-offset-2"
          >
            <ImagePlus className="h-5 w-5" />
            <span className="text-[10px]">Add Image</span>
          </label>
        </div>
        <p className="text-xs text-muted-foreground">
          First image will be used as the main product image.
        </p>
      </div>

      <div className="space-y-1">
        <Label htmlFor={`${fileInputId}-name`}>
          Name <span className="text-destructive">*</span>
        </Label>
        <Input
          id={`${fileInputId}-name`}
          value={form.name}
          onChange={(e) => onChange('name', e.target.value)}
          placeholder="Product name"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label htmlFor={`${fileInputId}-regular-price`}>
            Regular Price ({currency})
          </Label>
          <Input
            id={`${fileInputId}-regular-price`}
            type="number"
            step="0.01"
            value={form.regular_price}
            onChange={(e) => onChange('regular_price', e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`${fileInputId}-sale-price`}>
            Sale Price ({currency})
          </Label>
          <Input
            id={`${fileInputId}-sale-price`}
            type="number"
            step="0.01"
            placeholder="Leave empty for no sale"
            value={form.sale_price}
            onChange={(e) => onChange('sale_price', e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label htmlFor={`${fileInputId}-sku`}>SKU</Label>
          <Input
            id={`${fileInputId}-sku`}
            value={form.sku}
            onChange={(e) => onChange('sku', e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor={`${fileInputId}-qty`}>Stock Quantity</Label>
          <Input
            id={`${fileInputId}-qty`}
            type="number"
            placeholder="Leave empty = unmanaged"
            value={form.stock_quantity}
            onChange={(e) => onChange('stock_quantity', e.target.value)}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label>Status</Label>
          <Select
            value={form.status}
            onValueChange={(v) => onChange('status', v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WOO_STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label>Stock Status</Label>
          <Select
            value={form.stock_status}
            onValueChange={(v) => onChange('stock_status', v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {WOO_STOCK_STATUS_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-1">
        <Label htmlFor={`${fileInputId}-short-desc`}>Short Description</Label>
        <Textarea
          id={`${fileInputId}-short-desc`}
          rows={3}
          value={form.short_description}
          onChange={(e) => onChange('short_description', e.target.value)}
        />
      </div>
    </div>
  );
}
